import { StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/** Compact, automatic recommendations inside the existing cart drawer. */
class CartRecommendations extends HTMLElement {
  #request;
  #timer;
  #mobileLayout = window.matchMedia('(max-width: 989px)');
  #expanded = false;

  connectedCallback() {
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#onCartUpdate);
    this.addEventListener('click', this.#toggleDisclosure);
    this.#mobileLayout.addEventListener('change', this.#syncDisclosure);
    this.#syncDisclosure();
    this.refresh();
  }

  disconnectedCallback() {
    this.#request?.abort();
    clearTimeout(this.#timer);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#onCartUpdate);
    this.removeEventListener('click', this.#toggleDisclosure);
    this.#mobileLayout.removeEventListener('change', this.#syncDisclosure);
  }

  #toggleDisclosure = (event) => {
    if (!this.#mobileLayout.matches || !(event.target instanceof Element) || !event.target.closest('.cart-drawer__recommendations-toggle')) return;
    this.#expanded = !this.#expanded;
    this.#syncDisclosure();
  };

  #syncDisclosure = () => {
    const toggle = this.querySelector('.cart-drawer__recommendations-toggle');
    const panel = this.querySelector('.cart-drawer__recommendations-panel');
    if (!toggle || !panel) return;
    const expanded = !this.#mobileLayout.matches || this.#expanded;
    toggle.setAttribute('aria-expanded', String(this.#mobileLayout.matches && this.#expanded));
    panel.inert = !expanded;
    panel.setAttribute('aria-hidden', String(!expanded));
  };

  #onCartUpdate = (event) => {
    Promise.resolve(event.promise).then(() => {
      clearTimeout(this.#timer);
      this.#timer = setTimeout(() => {
        if (this.isConnected) this.refresh();
      }, 150);
    }).catch(() => {});
  };

  async refresh() {
    this.#request?.abort();
    this.#request = new AbortController();
    const { signal } = this.#request;
    const root = window.Shopify?.routes?.root || '/';
    const list = this.querySelector('.cart-drawer__recommendations-list');
    const error = this.querySelector('.cart-drawer__recommendations-error');
    if (!list || !error) return;

    try {
      const cartResponse = await fetch(`${root}cart.js`, { signal, credentials: 'same-origin' });
      if (!cartResponse.ok) throw new Error('Cart unavailable');
      const cart = await cartResponse.json();
      const cartIds = new Set(cart.items.map((item) => Number(item.product_id)));
      const seedIds = [...cartIds].slice(0, 2);
      if (!seedIds.length) {
        this.hidden = true;
        return;
      }

      const products = [];
      const seen = new Set(cartIds);
      for (const seedId of seedIds) {
        const url = `${root}recommendations/products.json?product_id=${encodeURIComponent(seedId)}&limit=10&intent=related`;
        const response = await fetch(url, { signal, credentials: 'same-origin' });
        if (!response.ok) continue;
        const data = await response.json();
        for (const product of data.products || []) {
          const id = Number(product.id);
          if (!product.available || !product.featured_image || seen.has(id)) continue;
          seen.add(id);
          products.push(product);
          if (products.length === 6) break;
        }
        if (products.length === 6) break;
      }
      if (signal.aborted) return;
      list.replaceChildren(...products.map((product) => this.#card(product, cart.currency)));
      error.hidden = true;
      this.hidden = products.length === 0;
    } catch (reason) {
      if (reason?.name === 'AbortError') return;
      list.replaceChildren();
      this.hidden = true;
    }
  }

  #card(product, currency) {
    const item = document.createElement('div');
    item.className = 'cart-drawer__recommendation';
    item.setAttribute('role', 'listitem');
    const href = new URL(product.url || `/products/${product.handle}`, location.origin);
    const link = document.createElement('a');
    link.href = href.pathname + href.search;
    const image = document.createElement('img');
    image.className = 'cart-drawer__recommendation-image';
    image.src = product.featured_image.startsWith('//') ? `${location.protocol}${product.featured_image}` : product.featured_image;
    image.alt = '';
    image.loading = 'lazy';
    image.width = 154;
    image.height = 154;
    const information = document.createElement('span');
    information.className = 'cart-drawer__recommendation-information';
    const title = document.createElement('span');
    title.className = 'cart-drawer__recommendation-title';
    title.textContent = product.title;
    const price = document.createElement('span');
    price.className = 'cart-drawer__recommendation-price';
    price.textContent = formatMoney(product.price, this.dataset.moneyFormat || '{{amount}}', currency);
    information.append(title, price);
    link.append(image, information);
    const variants = product.variants || [];
    const variant = variants[0];
    const canAdd = variants.length === 1 && variant.available && !product.requires_selling_plan && !variant.requires_selling_plan && variant.quantity_rule?.min === 1 && variant.quantity_rule?.increment === 1;
    const action = document.createElement(canAdd ? 'button' : 'a');
    action.className = 'cart-drawer__recommendation-action';
    if (canAdd) {
      action.type = 'button';
      action.setAttribute('aria-label', `Ajouter ${product.title} au panier`);
      action.addEventListener('click', () => this.#add(action, variant.id));
    } else {
      action.href = link.href;
      action.setAttribute('aria-label', `Choisir les options de ${product.title}`);
    }
    const template = this.querySelector('template[data-plus-icon]');
    action.append(template?.content.firstElementChild?.cloneNode(true) || document.createTextNode('+'));
    item.append(link, action);
    return item;
  }

  async #add(button, variantId) {
    if (button.disabled) return;
    const error = this.querySelector('.cart-drawer__recommendations-error');
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    error.hidden = true;
    try {
      if (!window.Shopify?.actions?.updateCart) throw new Error('Cart action unavailable');
      const result = await window.Shopify.actions.updateCart(
        { lines: [{ merchandiseId: String(variantId), quantity: 1 }] },
        { event: { context: 'cart' } }
      );
      if (result?.userErrors?.length) throw new Error(result.userErrors[0].message);
      requestAnimationFrame(() => document.querySelector('#cart-drawer .theme-drawer__close-button')?.focus({ preventScroll: true }));
    } catch (reason) {
      error.textContent = reason?.message || 'Impossible d’ajouter ce produit. Réessayez.';
      error.hidden = false;
      this.hidden = false;
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('cart-recommendations')) {
  customElements.define('cart-recommendations', CartRecommendations);
}
